import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import Modal from '../../../../common/_custom/Modal/Modal';
import { Button } from '../../../../common/_custom';
import TextAreaEditor from '../../../../common/TextEditor/TextAreaEditor';
import { slateToHtml } from '../../../../common/TextEditor/utils/SlateSerialisationUtil';
import { processImagesBeforeSave } from '../../../../common/PlateEditor/services/imageUploadService';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import AttachmentIcon from '../../../../assets/Svg/AttachmentIcon';
import OnePagerGenerate from '../../../../common/ManageDealDrawer/OnePagerGenerateButton/OnePagerGenerate';
import UploadCard, { UploadCardRef } from '../../../../modules/Company/UploadJourney/components/UploadCard/UploadCard';
import {
    AICA_CREATE_DEAL_DETAILS,
    FILE_SHARE_STATUS,
    MNEMOSYNE_DEAL_ATTACHMENT_FLOW_ID,
    UPLOAD_FILES_PREFIXES,
} from '../../../../common/ManageDealDrawer/DealDrawerConstants';
import { useOutsideClick } from '../../../../utils/constants/hooks';
import { useAppSelector } from '../../../../app/hooks';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { crossIconBlack, plusPurpleCircle } from '../../../../assets/hostedassets';
import { downloadFile } from '../../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_EMAIL_ATTACHMENT, AICA_ONE_PAGER_ATTACHMENT } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { categoryIdFromDealConfig } from './dealConfigCategoryId';
import styles from './ShareWithLenderModal.module.scss';

interface DocItem {
    docId: number;
    documentName: string;
    action: string;
}

interface ShareWithLenderModalProps {
    open: boolean;
    onClose: () => void;
    onSend?: (processedNote?: string) => Promise<void>;
    isTransitioning?: boolean;
    hideContent?: boolean;
    assesseeOrgId?: string;
    flowId?: number;
    addDealNote?: (note: string) => void;
    updateDealDocuments?: (documents: Array<{ documentName: string; docId: string | number; action: string }>) => void;
    companyName?: string;
    selectedCompany?: any;
    dealId?: string;
}

const { ONEPAGER_PREFIX, ATTACHMENT_PREFIX } = UPLOAD_FILES_PREFIXES;

function ShareWithLenderModal({
    open,
    onClose,
    onSend,
    hideContent = false,
    addDealNote,
    updateDealDocuments,
    assesseeOrgId,
    flowId,
    companyName,
    selectedCompany,
    dealId,
}: ShareWithLenderModalProps) {
    const [isSendLoading, setIsSendLoading] = useState(false);
    const [docs, setDocs] = useState<DocItem[]>([]);
    const [showAttachmentDropdown, setShowAttachmentDropdown] = useState(false);
    const [categoryId, setCategoryId] = useState<number | undefined>(undefined);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const docsDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const latestSlateValueRef = useRef<any[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const uploadCardRef = useRef<UploadCardRef>(null);

    const onePagerState = useAppSelector((state) => state.investor.onePager);
    const user = useAppSelector((state) => state.user);
    const platformData = useAppSelector((state) => state.user.domainMetadata);

    /**
     * FIX: Fetch categoryId ONLY when modal opens
     * Prevents infinite re-renders
     */
    useEffect(() => {
        if (!open) return;
        setCategoryId(undefined);
        let cancelled = false;

        getDealFormConfig(
            {
                accountType: user.userData.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: user.userData.primaryEmail,
                configName: AICA_CREATE_DEAL_DETAILS,
            },
            {
                onSuccess: (configData) => {
                    if (cancelled) return;
                    const id = categoryIdFromDealConfig(configData);
                    if (id == null) {
                        setCategoryId(undefined);
                        toast.error('Could not find attachment settings in deal config.');
                        return;
                    }
                    setCategoryId(id);
                },
                onError: () => {
                    if (cancelled) return;
                    setCategoryId(undefined);
                    toast.error('Could not load attachment settings. Try attaching the One Pager again.');
                },
            },
        );
        return () => {
            cancelled = true;
        };
    }, [open]);

    /** Close dropdown when clicking outside */
    const handleOutsideClick = useCallback(() => {
        if (!onePagerState?.open) setShowAttachmentDropdown(false);
    }, [onePagerState?.open]);

    useOutsideClick(dropdownRef, handleOutsideClick);

    const handleDocWithPrefixUpdate = useCallback((fileInfo: { docId: number; fileName: string }, documentPrefix: string) => {
        const updateFileObj: DocItem = {
            docId: fileInfo?.docId,
            documentName: `${documentPrefix}${fileInfo?.fileName}`,
            action: FILE_SHARE_STATUS.SEND_TO_INVESTOR,
        };

        setDocs((prevDocs) => {
            const docsToUpdate = [...prevDocs];
            const existingFileIndex = docsToUpdate.findIndex((doc) => doc.documentName?.includes(documentPrefix));

            if (existingFileIndex >= 0) {
                docsToUpdate[existingFileIndex] = updateFileObj;
            } else {
                docsToUpdate.push(updateFileObj);
            }

            return docsToUpdate;
        });
    }, []);

    const handleShareToggle = useCallback((actionStatus: string, documentPrefix: string) => {
        setDocs((prevDocs) =>
            prevDocs.map((doc) => ({
                ...doc,
                ...(doc?.documentName?.includes(documentPrefix) ? { action: actionStatus } : {}),
            })),
        );
    }, []);

    const onePagerExistingData = useMemo(() => {
        return docs.find((doc) => doc.documentName?.includes(ONEPAGER_PREFIX));
    }, [docs]);

    const handleUploadMetadataUpdate = useCallback((fileInfo: { docId: number; fileName: string }) => {
        const updateFileObj: DocItem = {
            docId: fileInfo?.docId,
            documentName: `${ATTACHMENT_PREFIX}${fileInfo?.fileName}`,
            action: FILE_SHARE_STATUS.SEND_TO_INVESTOR,
        };

        setDocs((prevDocs) => [...prevDocs, updateFileObj]);
    }, []);

    const handleUploadFileDelete = useCallback((fileInfo: { docId: number }) => {
        setDocs((prevDocs) => prevDocs.filter((doc) => doc.docId !== fileInfo.docId));
    }, []);

    const attachmentFiles = useMemo(() => {
        return docs
            .filter((doc) => doc.documentName?.includes(ATTACHMENT_PREFIX))
            .map((doc) => ({
                docId: doc.docId,
                docName: doc.documentName,
            }));
    }, [docs]);

    /** Mixpanel tracking for one pager attachment toggle */
    const registerMixpanelForOnePagerAttachment = () => {
        Mixpanel.track(AICA_ONE_PAGER_ATTACHMENT, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName || companyName,
            user_role: user?.userData?.accountType,
            mandate_id: dealId,
            org_id: selectedCompany?.assesseeOrgId,
        });
    };
    const registerMixpanelForEmailAttachment = () => {
        Mixpanel.track(AICA_EMAIL_ATTACHMENT, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName,
            user_role: user?.userData?.accountType,
            mandate_id: dealId,
            org_id: selectedCompany?.assesseeOrgId,
        });
    };
    /** One-pager logic */
    const handleOnePagerMetadataUpdate = useCallback(
        (fileInfo: { docId: number; fileName: string }) => {
            handleDocWithPrefixUpdate(fileInfo, ONEPAGER_PREFIX);
        },
        [handleDocWithPrefixUpdate],
    );

    const handleOnePagerShareToggle = useCallback(
        (action: string) => {
            registerMixpanelForOnePagerAttachment();
            handleShareToggle(action, ONEPAGER_PREFIX);
        },
        [handleShareToggle, registerMixpanelForOnePagerAttachment],
    );

    /** Debounce sync of docs to dealTerms.documents */
    useEffect(() => {
        if (docsDebounceTimerRef.current) {
            clearTimeout(docsDebounceTimerRef.current);
        }

        docsDebounceTimerRef.current = setTimeout(() => {
            if (updateDealDocuments) {
                const formattedDocs = docs.map((doc) => ({
                    documentName: doc.documentName,
                    docId: String(doc.docId),
                    action: doc.action,
                }));
                updateDealDocuments(formattedDocs);
            }
        }, 500);

        return () => {
            if (docsDebounceTimerRef.current) {
                clearTimeout(docsDebounceTimerRef.current);
            }
        };
    }, [docs, updateDealDocuments]);

    const handleEditorChange = useCallback(
        (value: any[]) => {
            latestSlateValueRef.current = value;
            // Debounced sync to parent for draft state (may contain base64 during editing)
            // At Send time, processImagesBeforeSave replaces base64 with docIds before final save
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                if (addDealNote) addDealNote(slateToHtml(value));
            }, 500);
        },
        [addDealNote],
    );

    /** Cleanup */
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (docsDebounceTimerRef.current) clearTimeout(docsDebounceTimerRef.current);
        };
    }, []);

    return (
        <Modal open={open} onClose={onClose} size="flexible" persistent={true} className={styles.ModalContainer}>
            <div className={`${styles.ModalContent} ${hideContent ? styles.ContentFadeOut : ''}`}>
                <div className={styles.ModalHeader}>
                    <div className={styles.ModalTitle}>Email Lenders</div>
                </div>

                <div className={styles.UserInputTextLabel}>Add Note in email body</div>

                <div className={styles.UserInputText}>
                    <TextAreaEditor
                        onChange={handleEditorChange}
                        editorInitialData={[]}
                        wrapperStyles={{
                            backgroundColor: '#ffffff',
                            width: '100%',
                            maxWidth: '100%',
                            height: '150px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                            boxSizing: 'border-box',
                        }}
                        editorStyles={{
                            width: '100%',
                            height: '100%',
                            minHeight: 'inherit',
                            outline: 'none',
                            overflow: 'auto',
                            padding: '5px',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Attached Files Display */}
                {(onePagerExistingData?.action === FILE_SHARE_STATUS.SEND_TO_INVESTOR || attachmentFiles.length > 0) && (
                    <div className={styles.AttachedFilesContainer}>
                        {onePagerExistingData?.action === FILE_SHARE_STATUS.SEND_TO_INVESTOR && (
                            <div className={styles.FileChip}>
                                <span
                                    className={styles.FileChipName}
                                    onClick={() => {
                                        if (onePagerExistingData?.docId) {
                                            downloadFile(
                                                Number(onePagerExistingData.docId),
                                                () => {},
                                                onePagerExistingData.documentName?.replace(ONEPAGER_PREFIX, ''),
                                            );
                                        }
                                    }}
                                >
                                    {onePagerExistingData.documentName?.replace(ONEPAGER_PREFIX, '') || 'One_Pager.pdf'}
                                </span>
                                <img
                                    className={styles.FileChipClose}
                                    src={crossIconBlack}
                                    alt="remove"
                                    onClick={() => handleOnePagerShareToggle(FILE_SHARE_STATUS.SKIP_NOTIFICATION)}
                                />
                            </div>
                        )}
                        {attachmentFiles.map((file) => (
                            <div key={file.docId} className={styles.FileChip}>
                                <span
                                    className={styles.FileChipName}
                                    onClick={() => {
                                        downloadFile(Number(file.docId), () => {}, file.docName?.replace(ATTACHMENT_PREFIX, ''));
                                    }}
                                >
                                    {file.docName?.replace(ATTACHMENT_PREFIX, '')}
                                </span>
                                <img
                                    className={styles.FileChipClose}
                                    src={crossIconBlack}
                                    alt="remove"
                                    onClick={() => handleUploadFileDelete({ docId: file.docId })}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.ButtonContainer}>
                    <div className={styles.AttachmentWrapper} ref={dropdownRef}>
                        <div
                            className={styles.AttachmentIcon}
                            onClick={() => {
                                registerMixpanelForEmailAttachment();
                                setShowAttachmentDropdown(!showAttachmentDropdown);
                            }}
                        >
                            <AttachmentIcon />
                        </div>

                        {showAttachmentDropdown && (
                            <div className={styles.AttachmentDropdown}>
                                <OnePagerGenerate
                                    companyName={companyName}
                                    formData={{}}
                                    source="createDeal"
                                    categoryId={categoryId}
                                    flowId={flowId || MNEMOSYNE_DEAL_ATTACHMENT_FLOW_ID}
                                    metadataUpdate={handleOnePagerMetadataUpdate}
                                    onShareToggle={handleOnePagerShareToggle}
                                    existingData={onePagerExistingData}
                                    assesseeOrgId={assesseeOrgId}
                                    isLocked={categoryId == null}
                                    buttonText="One Pager"
                                    hideShareLabel={true}
                                    buttonStyles={{ background: 'white' }}
                                />

                                <div
                                    className={styles.UploadOption}
                                    onClick={() => {
                                        if (categoryId == null) return;
                                        uploadCardRef.current?.triggerFileSelect();
                                    }}
                                >
                                    <img src={plusPurpleCircle} alt="upload" height="16px" />
                                    <span>Upload</span>
                                </div>

                                <UploadCard
                                    ref={uploadCardRef}
                                    assesseeOrgId={assesseeOrgId || ''}
                                    categoryId={categoryId || 0}
                                    flowId={flowId || MNEMOSYNE_DEAL_ATTACHMENT_FLOW_ID}
                                    hideUpload={true}
                                    allowMultiple={true}
                                    metadataUpdate={handleUploadMetadataUpdate}
                                    fileDeleteHandler={handleUploadFileDelete}
                                    existingFiles={attachmentFiles as []}
                                    fileNamePrefix={ATTACHMENT_PREFIX}
                                    style={{ padding: 0 }}
                                    showAttachedFiles={false}
                                    appendTimestamp={true}
                                />
                            </div>
                        )}
                    </div>

                    <Button
                        text={isSendLoading ? <LoadingSpinner height="12px" color="#FFFFFF" /> : 'Send'}
                        onClick={async () => {
                            if (onSend) {
                                setIsSendLoading(true);
                                try {
                                    // Upload base64 images to S3, pass processed HTML directly to onSend
                                    // (can't rely on addDealNote state update flushing before onSend reads it)
                                    let processedNote: string | undefined;
                                    if (latestSlateValueRef.current?.length) {
                                        const processed = await processImagesBeforeSave(
                                            latestSlateValueRef.current,
                                            [],
                                            'share-deal-with-lender',
                                        );
                                        latestSlateValueRef.current = processed;
                                        processedNote = slateToHtml(processed);
                                        if (addDealNote) addDealNote(processedNote);
                                    }
                                    await onSend(processedNote);
                                } finally {
                                    setIsSendLoading(false);
                                }
                            }
                        }}
                        disabled={isSendLoading}
                        variant="primary"
                        style={{ marginTop: '0', width: '102px' }}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default ShareWithLenderModal;
